import {
  Button,
  FieldWrapper,
  FormField,
  FormGrid,
  Page,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { RoleWithScopes } from 'lib-shared-user-role';
import { memo, useMemo } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import { rolesApi } from 'apis/roles';
import { HookFormText } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { validateName } from 'utils/formValidator';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { RoleFormAuthorizationScopesField } from './components/authorizationScopes/roleFormAuthorizationScopesField';
import { SettingsAgentsRoleFormValues } from './components/settingsAgentsRoleForm.types';

type SettingsAgentsRoleFormProps = {
  roleWithScopes?: RoleWithScopes;
};

const formId = 'settings-agents-role-form';

export const SettingsAgentsRoleForm = memo(function SettingsAgentsRoleForm({
  roleWithScopes,
}: SettingsAgentsRoleFormProps): JSX.Element {
  const notify = useNotifications();
  const navigate = useAppNavigate();
  const isExtraLargeScreen = useBreakpointValue({ xl: true });

  const [createCustomRole] = rolesApi.useCreateCustomRoleMutation();
  const [updateCustomRole] = rolesApi.useUpdateCustomRoleMutation();
  const [updateCustomRoleScopes] = rolesApi.useUpdateCustomRoleScopesMutation();

  const defaultValues = useMemo(
    () => ({
      name: roleWithScopes?.role?.name,
      description: roleWithScopes?.role?.description,
      scopes: roleWithScopes?.role_scopes.map(({ scope }) => scope.value) ?? [],
    }),
    [roleWithScopes],
  );

  const form = useForm<SettingsAgentsRoleFormValues>({
    mode: 'onBlur',
    defaultValues,
  });

  const handleCreateRole = async (values: SettingsAgentsRoleFormValues) => {
    try {
      const {
        data: { role: createdRole },
      } = await createCustomRole(values).unwrap();

      notify(
        {
          title: 'Custom role created',
          text: `The custom role "${values.name}" has been created.`,
          icon: 'i-done',
          time: 3000,
        },
        NotificationType.Success,
      );

      navigate({
        path: '/settings/agents/roles/:roleType/:roleId/permissions',
        params: {
          path: {
            roleType: 'custom',
            roleId: createdRole.id,
          },
        },
      });

      ampli.settingsRolesCustomRoleCreated({
        hasDescription: !!values.description,
        scopes: values.scopes,
      });
    } catch (error) {
      notify(
        {
          title: 'Error while creating the custom role.',
        },
        NotificationType.Error,
      );
    }
  };

  const handleUpdateRole = async (values: SettingsAgentsRoleFormValues) => {
    if (!roleWithScopes) return;

    try {
      const { scopes, ...data } = values;

      const scopeAdditions = scopes.filter(
        (scope) => !defaultValues.scopes.includes(scope),
      );
      const scopeDeletions = defaultValues.scopes.filter(
        (scope) => !scopes.includes(scope),
      );

      const [{ data: updatedRole }] = await Promise.all([
        await updateCustomRole({
          roleId: roleWithScopes.role.id,
          data,
        }).unwrap(),

        await updateCustomRoleScopes({
          roleId: roleWithScopes.role.id,
          scopeAdditions,
          scopeDeletions,
        }).unwrap(),
      ]);

      notify(
        {
          title: 'Custom role updated',
          text: `Changes to the "${updatedRole.name}" custom role have been saved.`,
          icon: 'i-done',
          time: 3000,
        },
        NotificationType.Success,
      );

      ampli.settingsRolesCustomRoleEdited({
        hasDescription: !!values.description,
        scopeAdditions,
        scopeDeletions,
      });

      form.reset(form.getValues());
    } catch (error) {
      notify(
        {
          title: 'Error while updating the custom role.',
        },
        NotificationType.Error,
      );
    }
  };

  const handleValidSubmit: SubmitHandler<SettingsAgentsRoleFormValues> = async (
    values,
  ) => {
    if (!roleWithScopes) {
      await handleCreateRole(values);
    } else {
      await handleUpdateRole(values);
    }
  };

  const isEditing = !!roleWithScopes;
  const isCustomRole = !roleWithScopes?.role?.is_system_role;

  return (
    <Page.Section
      title={
        isEditing
          ? isCustomRole
            ? 'Edit custom role'
            : undefined
          : 'Create custom role'
      }
      controls={
        isCustomRole && (
          <Button
            type="submit"
            form={formId}
            isLoading={form.formState.isSubmitting}
            disabled={
              !form.formState.isDirty ||
              !form.formState.isValid ||
              form.formState.isSubmitting
            }
          >
            {roleWithScopes ? 'Save changes' : 'Create role'}
          </Button>
        )
      }
    >
      <FormProvider {...form}>
        <form
          id={formId}
          noValidate
          tabIndex={-1}
          onSubmit={form.handleSubmit(handleValidSubmit)}
        >
          <FormGrid>
            <FormField span={isExtraLargeScreen ? 4 : 6}>
              <HookFormText
                name="name"
                label="Name"
                control={form.control}
                rules={{
                  required: 'Name is required',
                  validate: validateName,
                }}
                componentProps={{
                  placeholder: 'Write a name for the custom role',
                  readOnly: !isCustomRole,
                  autoFocus: isCustomRole,
                  autoComplete: 'off',
                }}
              />
            </FormField>

            <FormField span={isExtraLargeScreen ? 4 : 6}>
              <HookFormText
                name="description"
                label="Description"
                control={form.control}
                componentProps={{
                  placeholder:
                    'Write a short description of the custom role and its purpose',
                  readOnly: !isCustomRole,
                  autoComplete: 'off',
                }}
              />
            </FormField>

            <FormField span={isExtraLargeScreen ? 4 : 6}>
              <FieldWrapper label="Permissions">
                <RoleFormAuthorizationScopesField
                  roleWithScopes={roleWithScopes}
                />
              </FieldWrapper>
            </FormField>
          </FormGrid>
        </form>
      </FormProvider>
    </Page.Section>
  );
});
